'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { isArchivedTracePreview, toasterApi } from '@terra/ui-design-system';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { useTraceActions } from '../../../hooks/useTraceActions';
import {
  isAbortableExecution,
  isRerunableExecution,
  isResumableExecution,
} from './executions-list.model';

const toTraceMap = (traces: readonly TracePreviewDto[]) =>
  new Map(traces.map((trace) => [trace.threadId, trace] as const));

export function useExecutionsActions({
  applicationId,
  getSelectedTraces,
  onComplete,
  onSelectionCleared,
}: {
  applicationId: string | undefined;
  getSelectedTraces: () => Promise<TracePreviewDto[]>;
  onComplete: () => void;
  onSelectionCleared: () => void;
}) {
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const tExecutions = useTranslations('designSystem.executions');
  const tBulkActions = useTranslations('proxy.executions.bulkActions');
  const [tracesToDelete, setTracesToDelete] = useState<TracePreviewDto[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResolvingSelection, setIsResolvingSelection] = useState(false);
  const traceActions = useTraceActions({ applicationId, onComplete });
  const {
    handleDeleteTrace,
    handleBulkDelete,
    handleBulkResume,
    handleBulkRerun,
    handleBulkAbort,
  } = traceActions;
  const showArchivedError = useCallback(() => {
    toasterApi.error({ title: tLifecycle('archivedExecutionActionTooltip') });
  }, [tLifecycle]);
  const resolveMutableSelection = useCallback(async (): Promise<
    TracePreviewDto[] | null
  > => {
    if (isResolvingSelection) {
      return null;
    }

    setIsResolvingSelection(true);

    try {
      const traces = await getSelectedTraces();

      if (traces.length === 0) {
        showArchivedError();

        return null;
      }

      return traces;
    } catch (error) {
      console.error('Failed to resolve selected executions:', error);
      toasterApi.error({ title: tExecutions('delete.error') });

      return null;
    } finally {
      setIsResolvingSelection(false);
    }
  }, [getSelectedTraces, isResolvingSelection, showArchivedError, tExecutions]);
  const requestDelete = useCallback(
    (trace: TracePreviewDto) => {
      if (isArchivedTracePreview(trace)) {
        showArchivedError();

        return;
      }

      setTracesToDelete([trace]);
    },
    [showArchivedError],
  );
  const requestBulkDelete = useCallback(async () => {
    const traces = await resolveMutableSelection();

    if (traces) {
      setTracesToDelete(traces);
    }
  }, [resolveMutableSelection]);
  const cancelDelete = useCallback(() => {
    setTracesToDelete([]);
  }, []);
  const confirmDelete = useCallback(async () => {
    const mutableTraces = tracesToDelete.filter(
      (trace) => !isArchivedTracePreview(trace),
    );
    const [firstTrace] = mutableTraces;

    if (!firstTrace) {
      return;
    }

    setIsDeleting(true);

    try {
      if (mutableTraces.length === 1) {
        const didDelete = await handleDeleteTrace(firstTrace);

        if (!didDelete) {
          return;
        }

        toasterApi.success({ title: tExecutions('delete.success') });
      } else {
        await handleBulkDelete(toTraceMap(mutableTraces));
      }

      setTracesToDelete([]);
      onSelectionCleared();
    } catch (error) {
      console.error('Failed to delete trace:', error);
      toasterApi.error({ title: tExecutions('delete.error') });
    } finally {
      setIsDeleting(false);
    }
  }, [
    handleBulkDelete,
    handleDeleteTrace,
    onSelectionCleared,
    tExecutions,
    tracesToDelete,
  ]);
  const runBulkMutation = useCallback(
    async ({
      isEligible,
      mutate,
    }: {
      isEligible: (trace: TracePreviewDto) => boolean;
      mutate: (traces: Map<string, TracePreviewDto>) => Promise<void>;
    }) => {
      const traces = await resolveMutableSelection();

      if (!traces) {
        return;
      }

      if (!traces.some(isEligible)) {
        toasterApi.warning({ title: tBulkActions('noEligibleExecutions') });

        return;
      }

      await mutate(toTraceMap(traces));
      onSelectionCleared();
    },
    [onSelectionCleared, resolveMutableSelection, tBulkActions],
  );

  return {
    ...traceActions,
    isDeleteDialogOpen: tracesToDelete.length > 0,
    pendingDeleteCount: tracesToDelete.length,
    isDeleting: isDeleting || traceActions.isBulkDeleting,
    isResolvingSelection,
    requestDelete,
    requestBulkDelete,
    cancelDelete,
    confirmDelete,
    bulkResume: () =>
      runBulkMutation({
        isEligible: isResumableExecution,
        mutate: handleBulkResume,
      }),
    bulkRerun: () =>
      runBulkMutation({
        isEligible: isRerunableExecution,
        mutate: handleBulkRerun,
      }),
    bulkAbort: () =>
      runBulkMutation({
        isEligible: isAbortableExecution,
        mutate: handleBulkAbort,
      }),
  };
}
